import { Environment } from '@util/Environment';
import { useQuery } from '@util/QueryParams';
import { isLocalhost } from '@util/RouterUtils';
import { useConfirmPageUnload } from '@util/UseConfirmPageUnload';
import React, { FunctionComponent, useEffect } from 'react';
import { Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import { SkipToRoute } from './components/SkipToRoute';
import { Demo } from './components/demo/Demo';
import { Onboard } from './components/pages/Onboard';
import { Pay } from './components/pages/Pay';
import { YouHaveAccess } from './components/pages/YouHaveAccess';
import { UrlKey } from './config/AssessmentUtils';
import { SharedScreenName } from './config/FlowTypes';
import { NervaControlScreenName } from './config/nerva/NervaControl';
import { skipToPage } from './helpers/SkipToPage';

export const Routes: FunctionComponent = () => {
  return (
    <LocalhostRoute>
      <AppRoutes />
    </LocalhostRoute>
  );
};

const AppRoutes: FunctionComponent = () => {
  const location = useLocation();
  const match = useRouteMatch();
  const query = useQuery();

  const path = match.path === '/' ? '' : match.path; // See LocalhostRoute. Prefix to paths in Route components.

  function appendFreeTrialQueryParam(): string {
    query.append(UrlKey.FREE_TRIAL, 'true'); // Checked in getPaymentTrialVariantProps
    return query.toString();
  }

  useConfirmPageUnload();

  return (
    <Switch>
      {Environment.DEVELOPMENT && (
        <Route path={`${path}/demo`}>
          <Demo />
        </Route>
      )}

      <Route exact path={`${path}/start`}>
        <Pay />
      </Route>

      <Route exact path={`${path}/youHaveAccess`}>
        <YouHaveAccess />
      </Route>

      <Redirect from={`${path}/pay`} to={{ ...location, pathname: `${path}/start` }} />
      <Redirect from={`${path}/email-pay`} to={{ ...location, pathname: `${path}/start` }} />
      <Redirect
        from={`${path}/free-trial`}
        to={{
          ...location,
          pathname: `${path}/start`,
          search: appendFreeTrialQueryParam(),
        }}
      />
      <Redirect
        from={`${path}/free-trial-results`}
        to={{
          ...location,
          pathname: `${path}/results`,
          search: appendFreeTrialQueryParam(),
        }}
      />

      <Route exact path={`${path}/results`}>
        <Results />
      </Route>

      <SkipToRoute path={`${path}/skipToAuth`} page={SharedScreenName.Auth} />
      <SkipToRoute path={`${path}/skipToPay`} page={SharedScreenName.Payment} />
      <SkipToRoute path={`${path}/downloadPage`} page={SharedScreenName.DownloadApp} />
      <SkipToRoute path={`${path}/skipToTrial`} page={SharedScreenName.WorksBest} />

      <Route path="/">
        <Onboard />
      </Route>
    </Switch>
  );
};

const Results: FunctionComponent = () => {
  useEffect(function initialiseStorePage() {
    skipToPage(NervaControlScreenName.IbsResultsDate);
  }, []);
  return <Onboard />;
};

/**
 * When hosted on localhost, e.g. for development and integration tests, routes are of the form
 * http://localhost:3000/<app>/<page> to allow all products to be tested from a single app.
 * When hosted remotely, routes will be simply <domain>/<page> with the app determined from the domain,
 * @see Environment.ts
 */
const LocalhostRoute: FunctionComponent = (props) => {
  return isLocalhost() ? (
    <Switch>
      <Route path={`/${Environment.APP}`}>{props.children}</Route>
    </Switch>
  ) : (
    <>{props.children}</>
  );
};
